import { useAbortController } from "../hooks/useAbortController";
import axios from "axios";
import {getHeadersConfig} from "../utils/config";
import {DataGridPanel} from "../datagrid/DataGridPanel";
import FiltersPanel from "../panels/FiltersPanel";
import "bootstrap/dist/css/bootstrap.min.css";
import ErrorHandler from "../utils/ErrorHandler";
import {PageName,ColumnIds, UserPermissions, DataType, TrafficSourceResponseDto, DemandSourcesResponseDto} from "../utils/types";
import {ProductsCampaignsDto,CLinkOwnerResponseDto} from "../utils/types";
import {PlacementResponseDto,SearchResponseDto,ResponseStatus} from "../utils/types";
import {datagridColumns, datagridNumericWidth,datagridPercentageWidth, styleCategorical} from "../utils/datagridCommon";
import {PagesHelper} from "../common/PagesHelper";
import StateHelper from "../utils/StateHelper";
import {CommonRequests} from "../common/CommonRequests";
import { FiltersHelper } from "../common/FiltersHelper";
import { joinURLs } from "../utils/urlUtil";
import { TrafficSources } from "../services/TrafficSources";
import { useEffect, useState, Dispatch, SetStateAction, useMemo } from "react";
import { useLocation } from "react-router-dom";
import urlParser from "../utils/urlParser";
import debounce from "lodash/debounce";

const loadProductsPageColumns = () => {
  datagridColumns.set(ColumnIds.productID, { id: ColumnIds.productID, visible: true, name: "Product ID", showQuickFilterMenu : true, isGroupByColumn: true, align: "left", type: DataType.STRING,style: { minWidth: 100, maxWidth: 150, ...styleCategorical }});
  datagridColumns.set(ColumnIds.campaignID, { id: ColumnIds.campaignID, visible: true, name: "Campaign ID", showQuickFilterMenu : true, isGroupByColumn: true, align: "left", type: DataType.STRING,style: { minWidth: 100, maxWidth: 150, ...styleCategorical }});
  datagridColumns.set(ColumnIds.campaignSource, { id: ColumnIds.campaignSource, visible: true, name: "Campaign Source", showQuickFilterMenu : true, isGroupByColumn: true, align: "left", type: DataType.STRING,style: { minWidth: 100, maxWidth: 150, ...styleCategorical }});
  datagridColumns.set(ColumnIds.productViews, { id: ColumnIds.productViews, visible: true, name: "Product Views", showQuickFilterMenu : false, isGroupByColumn: false, align: "right", type: DataType.NUMBER, style: datagridNumericWidth });
  datagridColumns.set(ColumnIds.apptapMargin, { id: ColumnIds.apptapMargin, name: "Margin", avgDivideByNumDate: true, visible: true,  showQuickFilterMenu: false, isGroupByColumn: false, align: "right", type: DataType.PERCENTAGE, precision: 0, style: datagridPercentageWidth });
  datagridColumns.set(ColumnIds.postConversionEventRate, {id: ColumnIds.postConversionEventRate, name: "Post Conversion Events Rate", avgDivideByNumDate: true, visible: true , showQuickFilterMenu : false, isGroupByColumn: false, align: "right", type: DataType.PERCENTAGE, precision: 2, style: datagridPercentageWidth });
  datagridColumns.set(ColumnIds.creativeId, {id: ColumnIds.creativeId, name: "Creative ID", visible: true , showQuickFilterMenu : false, isGroupByColumn: true, align: "left", type: DataType.STRING, style: { minWidth: 80, maxWidth: 150, ...styleCategorical } });
  datagridColumns.set(ColumnIds.campaignEventType, {id: ColumnIds.campaignEventType, name: "Campaign Event Type", visible: true , showQuickFilterMenu : false, isGroupByColumn: true, align: "left", type: DataType.STRING, style: { minWidth: 50, maxWidth: 100 }});
  datagridColumns.set(ColumnIds.campaignEventId, {id: ColumnIds.campaignEventId, name: "Campaign Event ID", visible: true , showQuickFilterMenu : false, isGroupByColumn: true, align: "left", type: DataType.STRING, style: { minWidth: 50, maxWidth: 100, ...styleCategorical } });
  datagridColumns.set(ColumnIds.campaignEventsReceived, { id: ColumnIds.campaignEventsReceived, name: "Campaign Events Received", visible: true, axisId: "clicks", showQuickFilterMenu: false, isGroupByColumn: false, align: "right", type: DataType.NUMBER, precision: 0, style: datagridNumericWidth });
  datagridColumns.set(ColumnIds.otherEventsRate, { id: ColumnIds.otherEventsRate, name: "Other Events Rate", avgDivideByNumDate: true, visible: true, showQuickFilterMenu: false, isGroupByColumn: false, align: "right", type: DataType.PERCENTAGE, precision: 1, style: datagridPercentageWidth });
};

const clear = (setSearchResponseDto:any, setCurrentPage:any) => {
  setSearchResponseDto(null);
  setCurrentPage(1);    
}

const stats_products_GET = (currentUser:any, setSearchResponseDto:any, setCurrentPage:any, updateHistory:boolean = true, abortController?: AbortController) => {

  FiltersHelper.checkRequiredGroupByAndPermissions(currentUser, PageName.products);
  if (updateHistory) PagesHelper.historyPushFromPageState();

  const queryParams = PagesHelper.getQueryParameters(PageName.products);
  const config = getHeadersConfig();
  const apiUrl = joinURLs("2/stats/products", process.env.REACT_APP_REPORTING_API_BASE_URL);

  const responseDto : SearchResponseDto = {
    status : ResponseStatus.LOADING, 
    error : null,
    data : null,
    query : null,
    metadata : null
  };
  setSearchResponseDto(responseDto);

  axios
    .get(apiUrl, {...config, params: queryParams, signal: abortController?.signal })
    .then(function (response: any) {

      if (response.data?.data) {
        const responseDto : SearchResponseDto = response.data;
        responseDto.status = ResponseStatus.OK; 
        responseDto.error = null;
        setSearchResponseDto(responseDto);
        setCurrentPage(1);

      } else {
        clear(setSearchResponseDto, setCurrentPage);
      }
    })
    .catch(function (err) {
      // handle error
      if (!axios.isCancel(err)) {
        clear(setSearchResponseDto, setCurrentPage);
        const isLoggedOut = ErrorHandler.handleAuthError(err);
        if (!isLoggedOut) {
          const responseDto : SearchResponseDto = {
            status : ResponseStatus.ERROR, 
            error : err?.response?.data?.errorMessage,           
            data : null,
            query :  null,
            metadata : null
          };
          setSearchResponseDto(responseDto);
        }
      }
    });
};

const loadCLinks = async (currentUser:any, setCLinkOwnersDto:any) => {
  if (currentUser && currentUser.filterFields && currentUser.filterFields.includes("clinksInclude") && (currentUser.permissions.includes(UserPermissions.appptapDataCLinks) || currentUser.permissions.includes(UserPermissions.appptapDataCLinksOwners))) {
    const dto = await CommonRequests.searchCLInkOwners("");
    setCLinkOwnersDto(dto);
  }
};

const loadDemandSources = async (
  abortController: AbortController,
  setDemandSourcesDto: Dispatch<SetStateAction<DemandSourcesResponseDto>>
) => {
  try {
    await PagesHelper.loadDemandSourcesHelper(abortController, setDemandSourcesDto);
  } catch (error) {
    // Only log non-cancellation errors
    if (!axios.isCancel(error)) {
      console.error('Error loading demand sources:', error);
    }
  }
};

function ProductsPage(props : any) {
  const location = useLocation();
  const { createController: createStatsController } = useAbortController();
  const { createController: createTrafficSourcesController } = useAbortController();
  const { createController: createDemandSourcesController } = useAbortController();
  const [searchResponseDto, setSearchResponseDto] = useState<SearchResponseDto>(null);
  const [currentPage,setCurrentPage] = useState(1);
  const [trafficSourceResponseDto, setTrafficSourceResponseDto] = useState<TrafficSourceResponseDto>(null);
  const [placementsResponseDto, setPlacementsResponseDto] = useState<PlacementResponseDto>(null);
  const [placementsIsLoading, setPlacementsIsLoading] = useState<boolean>(false);
  const [productsDto, setProductsDto] = useState<ProductsCampaignsDto>(null);
  const [productsIsLoading, setProductsIsLoading] = useState<boolean>(false);
  const [campaignsDto, setCampaignsDto] = useState<ProductsCampaignsDto>(null);
  const [campaignsIsLoading, setCampaignsIsLoading] = useState<boolean>(false);

  const [cLinkOwnerstsIsLoading, setCLinkOwnersIsLoading] = useState<boolean>(false);
  const [cLinkOwnersDto, setCLinkOwnersDto] = useState<CLinkOwnerResponseDto>(null);
  const [demandSourcesDto, setDemandSourcesDto] = useState<DemandSourcesResponseDto>(null);

  // Debounced typeahead search for Products filter (300ms delay)
  const searchProducts = useMemo(
    () => debounce((query: string) => {
      CommonRequests.searchProducts(
        query,
        setProductsDto,
        setProductsIsLoading
      );
    }, 300),
    []
  );

  // Debounced typeahead search for Campaigns filter (300ms delay)
  const searchCampaigns = useMemo(
    () => debounce((query: string) => {
      CommonRequests.searchCampaigns(
        query,
        setCampaignsDto,
        setCampaignsIsLoading
      );
    }, 300),
    []
  );

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      searchProducts.cancel();
      searchCampaigns.cancel();
    };
  }, [searchProducts, searchCampaigns]);

  loadProductsPageColumns();
  function search(updateHistory:boolean = true) {
    const controller = createStatsController();
    stats_products_GET(props.currentUser, setSearchResponseDto, setCurrentPage, updateHistory, controller);
  }

  StateHelper.setSearchFuntion(PageName.products, search );

  // Parse URL parameters on mount and when location changes
  useEffect(() => {
    urlParser.parseUrl(location.pathname + location.search, () => {});
  }, [location]);

  useEffect(() => {
    if (props.currentUser) {
      TrafficSources.getTrafficSources(createTrafficSourcesController(), setTrafficSourceResponseDto);
      const statsController = createStatsController();
      stats_products_GET(props.currentUser, setSearchResponseDto, setCurrentPage, false, statsController);
      const demandSourcesController = createDemandSourcesController();
      loadDemandSources(demandSourcesController, setDemandSourcesDto);
      loadCLinks(props.currentUser, setCLinkOwnersDto);
    }
  }, [props.currentUser, createStatsController, createTrafficSourcesController, createDemandSourcesController]);


  return (
    <>
      <FiltersPanel
        search={search}
        page={PageName.products}

        placementsIsLoading={placementsIsLoading}
        setPlacementsIsLoading={setPlacementsIsLoading}

        placementsResponseDto={placementsResponseDto}
        setPlacementsResponseDto={setPlacementsResponseDto}

        currentUser={props.currentUser}
        demandSourcesDto={demandSourcesDto}

        trafficSourceResponseDto={trafficSourceResponseDto}

        cLinkOwnerstsIsLoading={cLinkOwnerstsIsLoading}
        setCLinkOwnersIsLoading={setCLinkOwnersIsLoading}
        cLinkOwnersDto={cLinkOwnersDto}
        setCLinkOwnersDto={setCLinkOwnersDto}
        mostFrequentTags={[]}

        productsDto={productsDto}
        productsIsLoading={productsIsLoading}
        searchProducts={searchProducts}
        campaignsDto={campaignsDto}
        campaignsIsLoading={campaignsIsLoading}
        searchCampaigns={searchCampaigns}
      />
      <hr/>
      <DataGridPanel
        search={search}

        page={PageName.products}

        searchResponseDto={searchResponseDto}
        setSearchResponseDto={setSearchResponseDto}

        datagridColumns={datagridColumns}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}

        currentUser={props.currentUser}

        trafficSourceResponseDto={trafficSourceResponseDto}
        setTrafficSourceResponseDto={setTrafficSourceResponseDto}
      />
    </>
  );
}

export default ProductsPage;
